// softwareCommerceState.js — single source of truth for the three
// customer-facing software commerce states in the workstation flow.
//
//   PRICED         — Ur Designs sells the license AND the price is
//                    known and payable now. Contributes to the order
//                    subtotal/tax.
//   QUOTE_REQUIRED — Ur Designs sells the license but final pricing
//                    must be confirmed before payment. Rendered in the
//                    order as "Price to be confirmed"; MUST NEVER be
//                    treated as AWG 0 or silently payable.
//   EXTERNAL       — Customer obtains the license directly from the
//                    provider. Never contributes to the Ur Designs
//                    subtotal. Optionally we can offer setup support
//                    during workstation configuration.
//   UNKNOWN_SAFE   — Data-quality fallback. When the recommendation +
//                    catalog combination is inconsistent (e.g.
//                    sourcing=ur_designs + sales_mode=online_purchase
//                    but no positive price), we render as QUOTE_REQUIRED
//                    to fail safely instead of charging a zero total.
//
// Inputs:
//   recommendation — a `UseCaseRecommendedSoftware` entry from
//                    /api/use-cases/{slug} (has: name, power_slug,
//                    note, sourcing, setup_supported).
//   product        — the resolved software record from /api/software
//                    keyed by power_slug (has: name, tagline,
//                    sales_mode, price, price_currency, publisher,
//                    status, option_groups). May be null when the
//                    catalog has no matching product.
//
// Returns an object with:
//   state          — one of the constants above.
//   canAttach      — whether "Add to Workstation" is a valid action.
//                    True for PRICED and QUOTE_REQUIRED. False for
//                    EXTERNAL (customer buys elsewhere) and
//                    UNKNOWN_SAFE (until data is fixed).
//   allowsSubtotal — whether this line contributes to the payable
//                    subtotal. True only for PRICED.
//   configurable   — whether the product has option groups the
//                    customer needs to choose from before attaching.
//   linkable       — whether the workstation card should link to
//                    /software/{slug} to configure. True for PRICED
//                    and QUOTE_REQUIRED that have a resolvable
//                    catalog record.
//   publisher      — best-guess publisher name (falls back to
//                    recommendation.name) for EXTERNAL copy.

export const STATE = Object.freeze({
  PRICED: "priced",
  QUOTE_REQUIRED: "quote_required",
  EXTERNAL: "external",
  UNKNOWN_SAFE: "unknown_safe",
});

const isPositive = (v) => typeof v === "number" && isFinite(v) && v > 0;

export function resolveSoftwareCommerceState(recommendation, product) {
  const rec = recommendation || {};
  const sourcing = (rec.sourcing || "").trim().toLowerCase();
  const powerSlug = (rec.power_slug || "").trim();
  const catalogActive = !!(product && product.status !== "hidden");

  // Product-level EXTERNAL (Feb 2026) — the software's own record
  // declares it as external/free (e.g. Affinity 3 free integrated app).
  // This overrides any use-case-level sourcing signal: if the product
  // itself is external, it is external everywhere, standalone and
  // workstation-context alike. Never contributes to subtotal.
  if (product && (product.sales_mode || "").toLowerCase() === "external") {
    return {
      state: STATE.EXTERNAL,
      canAttach: false,
      allowsSubtotal: false,
      configurable: false,
      linkable: catalogActive,   // still linkable for setup-support explanation
      publisher: product.publisher || rec.name || "the provider",
      product,
    };
  }

  // Explicit EXTERNAL — customer buys direct from provider.
  // sourcing="direct" is always external, regardless of whether the
  // slug also happens to resolve in the catalog (used for products
  // we list for context but never charge for).
  if (sourcing === "direct") {
    return {
      state: STATE.EXTERNAL,
      canAttach: false,
      allowsSubtotal: false,
      configurable: false,
      linkable: false,
      publisher: (product && product.publisher) || rec.name || "the provider",
      product,
    };
  }

  // Ur-Designs-sold path. Auto-detect if sourcing is empty; explicit
  // sourcing="ur_designs" is honoured. Requires a resolvable catalog
  // record to know sales_mode + price authoritatively.
  const isUrDesigns =
    sourcing === "ur_designs" || (sourcing === "" && !!powerSlug && catalogActive);

  if (isUrDesigns && catalogActive) {
    const salesMode = (product.sales_mode || "request_quote").toLowerCase();
    const price = Number(product.price || 0);

    if (salesMode === "online_purchase") {
      // Price MUST be a real positive number before we treat this as
      // PRICED — a zero/missing price with online_purchase is a data
      // inconsistency; fail safely to QUOTE_REQUIRED.
      if (isPositive(price)) {
        return {
          state: STATE.PRICED,
          canAttach: true,
          allowsSubtotal: true,
          configurable: (product.option_groups || []).length > 0,
          linkable: true,
          publisher: product.publisher || "",
          product,
        };
      }
      return {
        state: STATE.UNKNOWN_SAFE,
        canAttach: true,
        allowsSubtotal: false, // NEVER treat as payable when data is inconsistent
        configurable: (product.option_groups || []).length > 0,
        linkable: true,
        publisher: product.publisher || "",
        product,
      };
    }

    // sales_mode === "request_quote" (default) or "automated_fulfillment"
    // (reserved / unused) → quote required.
    return {
      state: STATE.QUOTE_REQUIRED,
      canAttach: true,
      allowsSubtotal: false,
      configurable: (product.option_groups || []).length > 0,
      linkable: true,
      publisher: product.publisher || "",
      product,
    };
  }

  // Recommendation with no resolvable catalog record — treat as a
  // recommendation-only (external in spirit; customer can't buy it
  // from us).
  return {
    state: STATE.EXTERNAL,
    canAttach: false,
    allowsSubtotal: false,
    configurable: false,
    linkable: false,
    publisher: rec.name || "the provider",
    product,
  };
}

// Convenience — returns the subtotal contribution for a given attached
// software line (as persisted in cart-line meta). Guarantees zero for
// non-PRICED states even if the line accidentally carries a price.
export function attachedSoftwareSubtotal(line) {
  if (!line || line.state !== STATE.PRICED) return 0;
  const p = Number(line.unit_price || 0);
  const q = Number(line.quantity || 1);
  if (!isPositive(p) || q < 1) return 0;
  return p * q;
}

// Session-storage helper — the workstation page owns the session
// entry keyed by `workstation:<slug>` and carries `{ platform,
// selectedAddons, attached_software }`. Centralized here so both
// pages share the same shape.
export const workstationSessionKey = (slug) => `workstation:${slug}`;

export function readWorkstationSession(slug) {
  try {
    const raw = sessionStorage.getItem(workstationSessionKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// Feb 2026 — DIRTY-GATED restoration. A workstation session is only
// treated as the customer's saved configuration when it carries an
// explicit `dirty: true` marker written after a real customer
// interaction (platform click, add-on toggle, external-setup toggle,
// software attach/remove). Any legacy/test/pre-fix payload lacking
// that marker is silently discarded on next read so the workstation
// configurator always renders truly-fresh state.
export function readWorkstationSessionIfDirty(slug) {
  const saved = readWorkstationSession(slug);
  if (!saved) return null;
  if (saved.dirty !== true) {
    try { sessionStorage.removeItem(workstationSessionKey(slug)); } catch {}
    return null;
  }
  return saved;
}

export function writeWorkstationSession(slug, patch) {
  if (!slug) return;
  try {
    const prev = readWorkstationSession(slug) || {};
    const next = { ...prev, ...patch };
    sessionStorage.setItem(workstationSessionKey(slug), JSON.stringify(next));
  } catch {}
}

// Attach or replace a software configuration on the workstation
// session. Keyed by power_slug so re-configuring the same product
// replaces the prior entry. This is an unambiguous explicit customer
// action → marks the session dirty so it survives the next
// readWorkstationSessionIfDirty() call.
export function attachSoftwareToWorkstation(slug, entry) {
  if (!slug || !entry || !entry.power_slug) return;
  const prev = readWorkstationSession(slug) || {};
  const prior = Array.isArray(prev.attached_software) ? prev.attached_software : [];
  const filtered = prior.filter((x) => x.power_slug !== entry.power_slug);
  const next = { ...prev, attached_software: [...filtered, entry], dirty: true };
  try {
    sessionStorage.setItem(workstationSessionKey(slug), JSON.stringify(next));
  } catch {}
}

export function removeAttachedSoftware(slug, powerSlug) {
  if (!slug || !powerSlug) return;
  const prev = readWorkstationSession(slug) || {};
  const prior = Array.isArray(prev.attached_software) ? prev.attached_software : [];
  const next = {
    ...prev,
    attached_software: prior.filter((x) => x.power_slug !== powerSlug),
    dirty: true,
  };
  try {
    sessionStorage.setItem(workstationSessionKey(slug), JSON.stringify(next));
  } catch {}
}

// Origin fallback — SOFTWARE-SCOPED (Feb 2026 correction). The old
// global "last workstation visited" key was replaced because it
// wrongly bled workstation context onto standalone software journeys
// (Home → Software mistakenly showed "Back to <last workstation>"
// even though the customer never entered from a workstation).
//
// The new key `software_origin:<software_slug>` is written ONLY at
// the moment the customer explicitly clicks a workstation → software
// CTA (i.e. attach-intent), keyed to the specific software slug they
// clicked. Only THAT software's page reads it back. Standalone
// navigation to any other software slug — including the previously-
// remembered one — will not find its origin here, so no stale
// workstation context leaks.
//
// TTL is short (10 minutes) to cover legitimate hard-refresh /
// direct history restoration during an active workstation-attach
// journey without persisting across obviously-different browsing
// sessions.
const ORIGIN_KEY_PREFIX = "software_origin:";
const ORIGIN_TTL_MS = 10 * 60 * 1000;

export function rememberOriginForSoftware(softwareSlug, workstationSlug, workstationName, intent) {
  if (!softwareSlug || !workstationSlug) return;
  try {
    sessionStorage.setItem(
      `${ORIGIN_KEY_PREFIX}${softwareSlug}`,
      JSON.stringify({
        workstation_slug: workstationSlug,
        workstation_name: workstationName || workstationSlug,
        // Feb 2026 — explicit customer intent (see item 6 in the
        // Software System Final Selection-State Audit):
        //   "attach" — new attach; the software has never been
        //              configured for this workstation. Fresh visit,
        //              zero selections, no restoration.
        //   "edit"   — customer explicitly clicked a Change link on
        //              an existing attachment. Restoration is
        //              permitted.
        // Defaults to "attach" if omitted so callers that don't yet
        // pass an intent don't accidentally trigger restoration.
        intent: intent === "edit" ? "edit" : "attach",
        at: Date.now(),
      }),
    );
  } catch {}
}

export function readOriginForSoftware(softwareSlug) {
  if (!softwareSlug) return null;
  try {
    const raw = sessionStorage.getItem(`${ORIGIN_KEY_PREFIX}${softwareSlug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.workstation_slug) return null;
    const ageMs = Date.now() - Number(parsed.at || 0);
    if (ageMs > ORIGIN_TTL_MS) {
      // Expired — clear to keep sessionStorage clean.
      try { sessionStorage.removeItem(`${ORIGIN_KEY_PREFIX}${softwareSlug}`); } catch {}
      return null;
    }
    // Only honour the marker when the current navigation is a
    // browser RELOAD of the software page. Any other navigation
    // (e.g. clicking Home then coming back to the same software
    // slug, following a nav link, opening from search) is treated
    // as a fresh standalone visit — the marker is auto-cleared so
    // it can't contaminate subsequent journeys.
    const isReload = (() => {
      try {
        const entries = performance.getEntriesByType && performance.getEntriesByType("navigation");
        if (entries && entries.length > 0) {
          return entries[0].type === "reload";
        }
        // Legacy Safari fallback.
        return performance.navigation && performance.navigation.type === 1;
      } catch {
        return false;
      }
    })();
    if (!isReload) {
      try { sessionStorage.removeItem(`${ORIGIN_KEY_PREFIX}${softwareSlug}`); } catch {}
      return null;
    }
    return {
      slug: parsed.workstation_slug,
      name: parsed.workstation_name || parsed.workstation_slug,
      // Default legacy markers (no intent recorded) to "attach" so
      // they never accidentally restore selections.
      intent: parsed.intent === "edit" ? "edit" : "attach",
    };
  } catch {
    return null;
  }
}

export function clearOriginForSoftware(softwareSlug) {
  if (!softwareSlug) return;
  try { sessionStorage.removeItem(`${ORIGIN_KEY_PREFIX}${softwareSlug}`); } catch {}
}

// Legacy no-op stubs — kept exported so any lingering imports don't
// break during the migration to the software-scoped API. Deliberately
// do nothing.
export function rememberWorkstationOrigin() { /* removed — use rememberOriginForSoftware */ }
export function readWorkstationOrigin() { return null; }
export function clearWorkstationOrigin() { /* removed — use clearOriginForSoftware */ }
